import { useStoreState } from '@src/model/hooks';
import { canViewPostDetails } from '@src/model/upvote/permission';
import { PostStatus } from '@src/store/contentEntities/model';
import { usePost } from '@src/store/posts/hooks/usePost';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

interface UsePostVisibilityGuardProps {
    postId: string;
    tokenName: string;
    enabled?: boolean;
}

/**
 * Hook that guards post visibility by redirecting non-moderators to /bbs/404
 * when they try to access hidden posts
 */
export const usePostVisibilityGuard = ({ postId, tokenName }: UsePostVisibilityGuardProps) => {
    const navigate = useNavigate();
    const post = usePost(postId);
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const isAuthenticating = useStoreState(state => state.user.isAuthenticating);

    useEffect(() => {
        if (!post || !postId) return;

        // Check if the post is hidden (one of the 3 hidden statuses)
        const hiddenStatuses = [PostStatus.DELETED_BY_TOKEN_ADMIN, PostStatus.DELETED_BY_PUBLISHER, PostStatus.DELETED_BY_DOMAIN_ADMIN];

        const isHiddenPost = hiddenStatuses.includes(post.status);

        if (!isHiddenPost) return; // Post is not hidden, allow access
        if (isAuthenticating) return; // Still authenticating, wait
        // Check if user has permission to view hidden posts
        const canView = canViewPostDetails(tokenName, postId);

        if (!canView) {
            // Non-moderator trying to access hidden post, redirect to 404
            navigate('/bbs/404', { replace: true });
        }
    }, [post, postId, tokenName, navigate, isLoggedIn, isAuthenticating]);

    return {
        isHiddenPost: post ? [PostStatus.DELETED_BY_TOKEN_ADMIN, PostStatus.DELETED_BY_PUBLISHER, PostStatus.DELETED_BY_DOMAIN_ADMIN].includes(post.status) : false,
        canViewPost: post ? canViewPostDetails(tokenName, postId) : false,
    };
};
